import { useState } from 'react';
import { Button, CircularProgress, Paper, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { CreateTaxRequestDTO } from '../../types/dtos/create-tax.dto';
import { normalizeTaxAmount } from '../../utils/tax-amount.util';
import { INVALID_TAX_AMOUNT_HELPER_TEXT } from '../../constants/tax-amount.constants';

type Props = {
  isSubmitting: boolean;
  onCreate: (taxData: CreateTaxRequestDTO) => Promise<boolean>;
};

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  createButton: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function CreateTaxCard({ isSubmitting, onCreate }: Props) {
  const classes = useStyles();

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [isPending, setIsPending] = useState(false);

  const trimmedName = name.trim();
  const normalizedAmount = normalizeTaxAmount(amount);
  const isAmountInvalid = amount.trim() !== '' && normalizedAmount === null;
  const isBusy = isSubmitting || isPending;
  const canCreate = !isBusy && trimmedName !== '' && normalizedAmount !== null;

  const handleCreate = async () => {
    if (!canCreate || normalizedAmount === null) return;

    setIsPending(true);
    try {
      const isCreated = await onCreate({ name: trimmedName, amount: normalizedAmount });
      if (!isCreated) return;

      setName('');
      setAmount('');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Paper className={classes.card}>
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        id="create-tax-name"
        label="Nom de la taxe"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        id="create-tax-amount"
        label="Taux (%)"
        value={amount}
        error={isAmountInvalid}
        helperText={isAmountInvalid ? INVALID_TAX_AMOUNT_HELPER_TEXT : 'La virgule est acceptée : 5,5 devient 5.5.'}
        onChange={(event) => setAmount(event.target.value)}
      />
      <Button
        size="small"
        variant="contained"
        className={classes.createButton}
        disabled={!canCreate}
        startIcon={isBusy ? <CircularProgress size={16} /> : null}
        onClick={handleCreate}
      >
        Créer
      </Button>
    </Paper>
  );
}
